/* AdminDangerZone — iter98.65
 *
 * One-button "Reset demo state" page nested under /admin. Calls the
 * existing prod_clean_seed wipe behind a two-step type-the-word
 * confirmation so a misclick can never trigger it.
 *
 * Flow:
 *   1. Auto-fetches GET /api/admin/danger-zone/preview on mount → renders
 *      a count of every row that WOULD be deleted vs every row preserved.
 *   2. Admin clicks "Begin reset" → confirmation panel appears asking
 *      them to type the literal word RESET into an input.
 *   3. Once the input == "RESET" the destructive button enables. Clicking
 *      it POSTs {confirm:"RESET"} to /reset-demo-state and renders the
 *      "what changed" diff in a result panel.
 *
 * Everything is admin-only at the backend layer; this UI just makes the
 * surface visible inside /admin/danger-zone.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Warning, Trash, CheckCircle, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import AdminDemoDataPanel from "@/components/admin/AdminDemoDataPanel";

export default function AdminDangerZone() {
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(true);
    const [stage, setStage] = useState("idle"); // idle | confirming | running | done
    const [confirmText, setConfirmText] = useState("");
    const [result, setResult] = useState(null);
    const [resetError, setResetError] = useState(null);

    const loadPreview = () => {
        setPreviewLoading(true);
        api.get("/admin/danger-zone/preview")
            .then((r) => setPreview(r.data))
            .catch((e) => toast.error(e.response?.data?.detail || "Couldn't load preview"))
            .finally(() => setPreviewLoading(false));
    };

    useEffect(() => {
        loadPreview();
    }, []);

    const beginReset = () => {
        setStage("confirming");
        setConfirmText("");
        setResetError(null);
    };

    const cancel = () => {
        setStage("idle");
        setConfirmText("");
        setResetError(null);
    };

    const runReset = async () => {
        if (confirmText !== "RESET") return;
        setStage("running");
        setResetError(null);
        try {
            const { data } = await api.post(
                "/admin/danger-zone/reset-demo-state",
                { confirm: "RESET" },
            );
            setResult(data);
            setStage("done");
            toast.success("Demo state reset complete");
        } catch (e) {
            setResetError(e.response?.data?.detail || "Reset failed");
            setStage("confirming");
        }
    };

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="admin-danger-zone">
            {/* Page header */}
            <div>
                <div className="overline text-rose-600 mb-1 inline-flex items-center gap-1.5">
                    <Warning size={11} weight="fill" /> Danger zone
                </div>
                <h1 className="font-display text-3xl font-bold tracking-tight">Reset demo state</h1>
                <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">
                    Wipes every test patient, case, appointment, payment, log and orphan doctor from this environment.
                    Seed accounts (admin · doctor · CoE · sales) and configuration tables (tiers · materials · promotions · practice locations · sales pipeline) stay intact.
                    The action is audit-logged.
                </p>
            </div>

            {/* Warning banner */}
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-5 flex items-start gap-3" data-testid="danger-zone-warning">
                <Warning size={22} weight="duotone" className="text-rose-700 shrink-0 mt-0.5" />
                <div className="text-sm">
                    <div className="font-bold text-rose-900 mb-1">This action is destructive and cannot be undone.</div>
                    <div className="text-rose-800/85 leading-relaxed">
                        Every case, patient, appointment, scan visit, payment, file, message, log and orphan doctor record will be permanently deleted.
                        The case-code + patient-code counters reset to 0 — your next real case will be <span className="font-mono font-semibold">IVQ-CRN-…-0001</span>.
                    </div>
                </div>
            </div>

            {/* Preview panel — what will be deleted vs preserved */}
            {previewLoading ? (
                <div className="rounded-2xl border border-border bg-card p-8 text-sm text-muted-foreground text-center" data-testid="danger-zone-loading">
                    Loading preview…
                </div>
            ) : preview && (
                <div className="grid md:grid-cols-2 gap-4">
                    <PreviewPanel
                        title="Will be deleted"
                        accent="rose"
                        icon={<Trash size={14} weight="duotone" />}
                        total={preview.total_test_rows}
                        rows={preview.will_delete}
                        testid="danger-zone-deletes"
                    />
                    <PreviewPanel
                        title="Will be preserved"
                        accent="emerald"
                        icon={<CheckCircle size={14} weight="fill" />}
                        rows={preview.preserved}
                        testid="danger-zone-preserves"
                    />
                </div>
            )}

            {/* Action surface — collapses through three states (idle, confirming, done) */}
            {stage === "idle" && (
                <div className="rounded-2xl border border-border bg-card p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="danger-zone-idle">
                    <div className="text-sm">
                        <div className="font-semibold">Ready when you are.</div>
                        <div className="text-xs text-muted-foreground mt-0.5">A confirmation prompt will appear before anything gets deleted.</div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={loadPreview}
                            className="rounded-full gap-1.5"
                            data-testid="danger-zone-refresh-btn"
                        >
                            <ArrowClockwise size={13} weight="bold" /> Refresh counts
                        </Button>
                        <Button
                            onClick={beginReset}
                            size="sm"
                            className="rounded-full bg-rose-600 hover:bg-rose-700 text-white gap-1.5"
                            data-testid="danger-zone-begin-btn"
                        >
                            <Trash size={13} weight="bold" /> Begin reset
                        </Button>
                    </div>
                </div>
            )}

            {stage === "confirming" && (
                <div className="rounded-2xl border border-rose-300 bg-rose-50/60 p-5 space-y-3" data-testid="danger-zone-confirming">
                    <div className="text-sm">
                        <div className="font-bold text-rose-900">Type <span className="font-mono px-1.5 py-0.5 bg-white border border-rose-300 rounded">RESET</span> to confirm.</div>
                        <div className="text-xs text-rose-800/80 mt-1">The button below stays disabled until the input matches exactly.</div>
                    </div>
                    <Input
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder="Type RESET"
                        className="font-mono h-11 max-w-xs"
                        autoFocus
                        data-testid="danger-zone-confirm-input"
                    />
                    {resetError && (
                        <div className="text-xs text-rose-700 font-medium" data-testid="danger-zone-reset-error">
                            {resetError}
                        </div>
                    )}
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={cancel}
                            className="rounded-full"
                            data-testid="danger-zone-cancel-btn"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={runReset}
                            size="sm"
                            disabled={confirmText !== "RESET"}
                            className="rounded-full bg-rose-600 hover:bg-rose-700 text-white gap-1.5 disabled:opacity-50"
                            data-testid="danger-zone-execute-btn"
                        >
                            <Trash size={13} weight="bold" /> Execute reset
                        </Button>
                    </div>
                </div>
            )}

            {stage === "running" && (
                <div className="rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground" data-testid="danger-zone-running">
                    Resetting demo state… this usually takes under a second.
                </div>
            )}

            {stage === "done" && result && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 space-y-3" data-testid="danger-zone-done">
                    <div className="flex items-center gap-2 text-emerald-800 font-semibold">
                        <CheckCircle size={18} weight="fill" />
                        Reset complete.
                    </div>
                    <DiffSummary diff={result.diff} />
                    <div className="flex items-center gap-2 pt-2 border-t border-emerald-200/70">
                        <Button
                            size="sm"
                            onClick={() => { setStage("idle"); setResult(null); loadPreview(); }}
                            className="rounded-full"
                            data-testid="danger-zone-back-btn"
                        >
                            Back to dashboard
                        </Button>
                    </div>
                </div>
            )}

            {/* iter100.244 — Demo data panel moved here from Overview.
                Sits at the bottom of the Danger Zone since seeding
                demo data is the natural counterpart to Reset above.
                After a wipe (or on a fresh env) you re-populate the
                dataset here. */}
            <div className="pt-4 border-t border-border" data-testid="danger-zone-seed-section">
                <div className="mb-3">
                    <div className="overline text-primary mb-1 inline-flex items-center gap-1.5">
                        <ArrowClockwise size={11} weight="fill" /> Demo data controls
                    </div>
                    <h2 className="font-display text-xl font-bold tracking-tight">Seed &amp; refresh demo dataset</h2>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        Populate this environment with demo patients, doctors, cases and appointments — the counterpart to Reset above.
                    </p>
                </div>
                <AdminDemoDataPanel />
            </div>
        </div>
    );
}


function PreviewPanel({ title, accent, icon, total, rows, testid }) {
    const accentClasses = {
        rose: "border-rose-200 bg-rose-50/40",
        emerald: "border-emerald-200 bg-emerald-50/40",
    }[accent];
    const labelClasses = {
        rose: "text-rose-800",
        emerald: "text-emerald-800",
    }[accent];

    const nonZeroEntries = Object.entries(rows || {}).filter(([, v]) => v && v > 0);
    const zeroCount = Object.values(rows || {}).filter((v) => !v || v <= 0).length;

    return (
        <div className={`rounded-2xl border ${accentClasses} p-5`} data-testid={testid}>
            <div className="flex items-center justify-between mb-3">
                <div className={`inline-flex items-center gap-1.5 font-semibold text-sm ${labelClasses}`}>
                    {icon} {title}
                </div>
                {total !== undefined && (
                    <span className={`text-xs font-mono px-2 py-0.5 rounded-full bg-white border ${accent === "rose" ? "border-rose-200 text-rose-700" : "border-emerald-200 text-emerald-700"}`}>
                        {total} rows
                    </span>
                )}
            </div>
            {nonZeroEntries.length === 0 ? (
                <div className="text-xs text-muted-foreground italic">
                    All counts zero — nothing in this bucket right now.
                </div>
            ) : (
                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                    {nonZeroEntries.map(([k, v]) => (
                        <div key={k} className="flex items-center justify-between gap-2">
                            <span className="text-muted-foreground truncate">{k}</span>
                            <span className="font-mono font-semibold tabular-nums">{v}</span>
                        </div>
                    ))}
                </div>
            )}
            {zeroCount > 0 && nonZeroEntries.length > 0 && (
                <div className="text-[10px] text-muted-foreground/70 mt-3 pt-2 border-t border-border/60">
                    + {zeroCount} other categories at 0
                </div>
            )}
        </div>
    );
}

function DiffSummary({ diff }) {
    if (!diff) return null;
    const deletedRows = Object.entries(diff.deleted || {}).filter(([, v]) => v && v > 0);
    const interesting = [
        ["doctors_removed", diff.doctors_removed],
        ["practice_locations_removed", diff.practice_locations_removed],
        ["uploads_purged", diff.uploads_purged],
    ].filter(([, v]) => v && v > 0);

    if (deletedRows.length === 0 && interesting.length === 0) {
        return (
            <div className="text-xs text-emerald-900/80">
                Nothing needed deleting — the database was already clean.
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <div className="text-xs font-semibold text-emerald-900 uppercase tracking-wider">What changed</div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                {deletedRows.map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between gap-2">
                        <span className="text-emerald-900/80 truncate">{k}</span>
                        <span className="font-mono font-semibold tabular-nums text-emerald-900">−{v}</span>
                    </div>
                ))}
                {interesting.map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between gap-2">
                        <span className="text-emerald-900/80 truncate">{k}</span>
                        <span className="font-mono font-semibold tabular-nums text-emerald-900">−{v}</span>
                    </div>
                ))}
            </div>
            {diff.counters_reset?.length > 0 && (
                <div className="text-[11px] text-emerald-900/80 pt-1.5 border-t border-emerald-200/60">
                    Counters reset: <span className="font-mono">{diff.counters_reset.join(", ")}</span>
                </div>
            )}
        </div>
    );
}
